<template>
  <div>
    <v-btn
      class="mr-2"
      block
      color="secondary"
      :loading="importing"
      :disabled="disabled"
      @click="dialogImport = true">
      <v-icon class="mr-1">
        mdi-upload
      </v-icon>
      <span>
        IMPORT FILE
      </span>
    </v-btn>
    <v-dialog
      v-model="dialogImport"
      max-width="500px"
      @click:outside="onCloseCsvDialog">
      <v-card>
        <v-card-title>Upload a File</v-card-title>

        <v-card-text class="pb-0">
          <p>
            <!-- คำเตือน! เมื่อทำการเลือกไฟล์จำนวนสินค้าจะรวมกับสินค้าก่อนหน้า -->
            คำเตือน! เมื่อทำการเลือกไฟล์สินค้าก่อนหน้าจะหายไป
          </p>
          <v-file-input
            v-model="selectedFile"
            label="Select CSV file"
            accept=".csv"
            color="secondary"
            class="my-2"
            outlined
            dense
            prepend-icon=""
            append-icon="mdi-upload"
            hide-details
            @focus="clearValidate"
            @change="validateCsv" />
          <p
            v-if="csvValidateErrors.length"
            class="text-bold">
            Total {{ csvValidateErrors.length }} errors.
          </p>
          <div
            v-if="csvValidateErrors.length"
            class="csv-fail">
            <p
              v-for="(errText, index) in csvValidateErrors"
              :key="index"
              class="red--text">
              {{ errText }}
            </p>
          </div>
          <WarehouseSelect
            v-model="warehouseSelected"
            :items="warehouses"
            item-text="name"
            item-value="id"
            :menu-props="{ offsetY: true }"
            label="Warehouses"
            return-object
            outlined />
          <p
            v-if="!isDisableConfirm"
            class="red--text mt-2">
            * คำเตือน! กรุณาตรวจสอบราคาสินค้าก่อนทำการยืนยัน
          </p>
          <div class="d-flex justify-end mt-4 mb-2">
            <v-btn
              class="text-decoration-underline"
              text
              small
              color="primary"
              @click="downloadTemplateCsv">
              Download Template
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="importing"
            @click="onCloseCsvDialog">
            Close
          </v-btn>
          <v-btn
            color="secondary"
            :loading="importing"
            :disabled="isDisableConfirm"
            @click="showConfirmation">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ExportToCsv } from 'export-to-csv'
import ProductProvider from '@/resources/ProductProvider'
import WarehouseSelect from '@/components/WarehouseSelect.vue'
import CurrencyRatesProvider from '@/resources/CurrencyRatesProvider'

const ProductService = new ProductProvider()
const CurrencyRatesService = new CurrencyRatesProvider()

const fileReader = new FileReader()
export default {
  components: {
    WarehouseSelect
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      masterHeaderCsv: [
        'Code',
        'Quantity',
        'Wholesale Price (THB)'
      ],
      dialogImport: false,
      selectedFile: null,
      csvValidateErrors: [],
      importing: false,
      warehouseSelected: null,
      currencyMapping: {
        thb: 35,
        foriegnCurrency: {
          isoCode: 'usd',
          label: 'usd',
          value: 1
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      mapWarehouse: 'Store/mapWarehouse',
      orderDetail: 'Order/orderDetail',
      orderSkus: 'Order/orderSkus',
      subOrders: 'Order/subOrders'
    }),
    warehouses () {
      return this.mapWarehouse.filter((v) => v.id !== 0)
    },
    channel () {
      return this.orderDetail.channel
    },
    isDisableConfirm () {
      return this.csvValidateErrors.length > 0 || !this.selectedFile || !this.warehouseSelected
    }
  },
  methods: {
    ...mapActions({
      setSkus: 'Order/setSkus',
      setOrderDetail: 'Order/setOrderDetail',
      setSnackbar: 'Components/setSnackbar'
    }),
    async getCurrencyRates () {
      try {
        const { data } = await CurrencyRatesService.getCurrencyRates({
          page: 1
        })

        if (!data?.results?.length) {
          return
        }

        const currency = data.results[0]
        this.currencyMapping = {
          thb: currency.thb,
          foriegnCurrency: {
            isoCode: currency.isoCode,
            label: currency.label,
            value: currency.value
          }
        }
      } catch (error) {
        console.error('getCurrencyRates', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error',
          timeout: 6000
        })
      }
    },
    isArrayEqual (arr1, arr2) {
      if (arr1.length !== arr2.length) {
        return false
      }

      const sorted1 = [...arr1].sort()
      const sorted2 = [...arr2].sort()

      return sorted1.every((val, index) => val === sorted2[index])
    },
    findDuplicates (arr) {
      return arr.filter((item, index) => arr.indexOf(item) !== index)
    },
    isNumberString (str) {
      return /^\d+(\.0+)?$/.test(str)
    },
    isFloatString (str) {
      return /^([0-9]*[.])?[0-9]+$/.test(str)
    },
    validateCsv () {
      this.csvValidateErrors = []

      if (!this.selectedFile) {
        return
      }

      const addRowText = (existingText, newText) => `${existingText ? ', ' : ''} ${newText}`

      fileReader.readAsText(this.selectedFile, 'utf8')
      fileReader.onload = async (readerEvent) => {
        try {
          const csvData = readerEvent.target.result
          const { headers, rows } = this.$parseCsv(csvData)
          const missingHeaders = this.masterHeaderCsv.filter((h) => !headers.includes(h))
          const duplicateField = this.findDuplicates(headers)

          if (!rows || !Array.isArray(rows) || !rows.length) {
            this.csvValidateErrors.push('The CSV contains no content. Please ensure the file has valid data.')
          }

          if (missingHeaders.length) {
            this.csvValidateErrors.push(`Header : Required headers are missing: ${missingHeaders.join(', ')}. Please add them.`)
          }

          if (duplicateField.length) {
            this.csvValidateErrors.push(`Header : ${duplicateField.join(', ')} fields are duplicated`)
          }

          if (this.csvValidateErrors.length === 0) {
            for (const [index, element] of rows.entries()) {
              let rowFailMsg = ''

              if (!element.Code) {
                rowFailMsg += addRowText(rowFailMsg, 'Code must be a valid')
              }

              if (!element.Quantity || !this.isNumberString(element.Quantity) || (Number(element.Quantity) <= 0)) {
                rowFailMsg += addRowText(rowFailMsg, 'Quantity must be a positive integer')
              }

              if (
                !element['Wholesale Price (THB)']
                || !this.isFloatString(element['Wholesale Price (THB)'])
                || (Number(element['Wholesale Price (THB)']) <= 0)
              ) {
                rowFailMsg += addRowText(rowFailMsg, 'Wholesale Price (THB) must be a valid')
              }

              // if (
              //   !element['Sale Price']
              //   || !this.isFloatString(element['Sale Price'])
              //   || (Number(element['Sale Price']) <= 0)
              // ) {
              //   rowFailMsg += addRowText(rowFailMsg, 'Sale Price must be a valid')
              // }

              // if (!element.Currency) {
              //   rowFailMsg += addRowText(rowFailMsg, 'Currency must be a valid')
              // }

              if (rowFailMsg) {
                rowFailMsg = `Row ${index + 2} : ${rowFailMsg}\n`
                this.csvValidateErrors.push(rowFailMsg)
              }
            }
          }
        } catch (e) {
          console.error('validateCsv', e)
          this.csvValidateErrors.push('Something wrong on CSV validate')
        }
      }
    },
    showConfirmation () {
      if (this.subOrders?.length) {
        this.$store.dispatch('Components/setModal', {
          value: true,
          title: 'Import Order',
          message: 'คำเตือน! เมื่อทำการยืนยัน ข้อมูลสินค้าก่อนหน้าจะหายไป',
          confirmText: 'Confirm',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onConfirmImport()
        })
      } else {
        this.onConfirmImport()
      }
    },
    async onConfirmImport () {
      try {
        this.importing = true

        // const selectedProducts = this.subOrders.reduce((acc, subOrder) => [...acc, ...subOrder?.skus], [])
        const selectedProducts = []
        const skuNotFounds = []
        const skuIncorrectPrices = []
        const skusFromCsv = []

        fileReader.readAsText(this.selectedFile, 'utf8')
        fileReader.onload = async (readerEvent) => {
          const csvData = readerEvent.target.result
          const { rows } = this.$parseCsv(csvData)

          for (const row of rows) {
            skusFromCsv.push(row)
          }

          const skuCodes = [...new Set(skusFromCsv.map((sku) => sku['Code']))]
          const { data: skus } = await ProductService.getSkusByCodesForB2B({ skuCodes })

          for (const [index, skuFromCsv] of skusFromCsv.entries()) {
            const skuInfo = skus.find((sku) => sku.code === skuFromCsv['Code'])

            if (skuInfo) {
              const duplicated = selectedProducts.findIndex((selected) => selected.code === skuInfo.code
                && selected.warehouse.id === this.warehouseSelected.id)
              const duplicatedImport = selectedProducts.findIndex((selected) => selected.code === skuInfo.code)

              const skuUnitPrice = skuInfo?.unitPrice || skuInfo.price
              const unitPrice = this.channel === 'b2b_usd' && !skuInfo?.unitPrice ? Math.ceil(skuUnitPrice / this.currencyMapping.thb) : skuUnitPrice
              const b2bUnitPrice = Number(skuFromCsv['Wholesale Price (THB)'])
              const b2bAmount = Number(skuFromCsv['Quantity'])

              if (unitPrice < b2bUnitPrice) {
                skuIncorrectPrices.push(
                  `Row ${index + 1} : ราคาสินค้า ${skuInfo.code} ต้องไม่เกิน ${unitPrice} ${this.channel === 'b2b_usd' ? 'USD' : 'THB'}`
                )
                // eslint-disable-next-line no-continue
                continue
              }

              if (duplicatedImport !== -1
                && selectedProducts[duplicatedImport].importUnitPrice !== 0
                && selectedProducts[duplicatedImport].importUnitPrice !== b2bUnitPrice) {
                skuIncorrectPrices.push(
                  `Row ${index + 1} : ราคาสินค้า ${skuInfo.code} ต้องไม่ต่างกัน`
                )
                // eslint-disable-next-line no-continue
                continue
              } else if (duplicated === -1) {
                const fullPrice = unitPrice * b2bAmount
                const price = b2bUnitPrice * b2bAmount
                const discount = fullPrice - price

                skuInfo.amount = b2bAmount
                skuInfo.unitPrice = unitPrice
                skuInfo.fullPrice = fullPrice
                skuInfo.price = price
                skuInfo.warehouse = this.warehouseSelected
                skuInfo.discountPercent = 0
                skuInfo.discountPrice = discount < 0 ? 0 : discount
                skuInfo.discountType = 'amount'

                selectedProducts.push({
                  ...skuInfo,
                  importAmount: b2bAmount,
                  importUnitPrice: b2bUnitPrice
                })
              } else {
                selectedProducts[duplicated].amount += b2bAmount
                selectedProducts[duplicated].importAmount += b2bAmount
                selectedProducts[duplicated].importUnitPrice = b2bUnitPrice

                const { amount, importAmount, importUnitPrice } = selectedProducts[duplicated]
                const totalImportAmount = importAmount || 0
                const totalImportUnitPrice = importUnitPrice || 0
                const rawAmount = amount - totalImportAmount

                const fullPrice = unitPrice * amount
                const price = (rawAmount * unitPrice) + (totalImportAmount * totalImportUnitPrice)
                const discount = fullPrice - price

                selectedProducts[duplicated].unitPrice = unitPrice
                selectedProducts[duplicated].fullPrice = fullPrice
                selectedProducts[duplicated].price = price
                selectedProducts[duplicated].discountPrice = discount < 0 ? 0 : discount
              }
            } else {
              skuNotFounds.push({
                ...skuFromCsv,
                row: index + 1
              })
            }
          }

          if (skuNotFounds.length) {
            skuNotFounds.forEach((v) => {
              this.csvValidateErrors.push(`Row ${v.row} : ไม่พบสินค้า ${v['Code']}`)
            })
          } else if (skuIncorrectPrices.length) {
            this.csvValidateErrors.push(...skuIncorrectPrices)
          } else {
            this.onSubmitImport(selectedProducts)
          }

          this.importing = false
        }
      } catch (error) {
        console.error('onConfirmImport', error)
        this.setSnackbar({
          value: true,
          message: 'ไฟล์ไม่สามารถใช้งานได้กรุณาตรวจสอบ',
          type: 'error',
          timeout: 6000
        })

        this.importing = false
      }
    },
    clearValidate () {
      this.selectedFile = null
      this.csvValidateErrors = []
    },
    onCloseCsvDialog () {
      this.dialogImport = false
      this.warehouseSelected = null
      this.clearValidate()
    },
    getDiscountByProductId (discounts, id) {
      const found = discounts.find((discount) => discount.id === id)
      return found?.discountPrice / found?.amount || 0
    },
    getUnitPrice (fullPrice, amount) {
      return fullPrice / amount || 0
    },
    mapSubOrders (skus) {
      const subOrders = []

      skus.forEach((sku) => {
        const index = subOrders.findIndex((item) => item.warehouse.id === sku.warehouse.id)
        const importAmount = sku?.importAmount || 0
        const importUnitPrice = sku?.importUnitPrice || 0
        const importPrice = importUnitPrice * importAmount
        const rawAmount = sku.amount - importAmount
        const unitPrice = rawAmount ? (sku.price - importPrice) / rawAmount : (sku.fullPrice / sku.amount)
        const price = (unitPrice * rawAmount) + importPrice
        const fullPrice = unitPrice * sku.amount
        const averageUnitPrice = Number((fullPrice / sku.amount).toFixed(2))
        const discount = fullPrice - price

        const payload = {
          importAmount,
          importUnitPrice,
          fullPrice,
          price,
          unitPrice: averageUnitPrice,
          discountPrice: discount < 0 ? 0 : discount,
          discountType: 'amount'
        }

        if (index === -1) {
          subOrders.push({
            preOrder: sku?.preOrder || false,
            skus: [
              {
                ...sku,
                ...payload
              }
            ],
            warehouse: sku.warehouse
          })
        } else {
          subOrders[index].skus.push({
            ...sku,
            ...payload
          })
        }
      })

      return subOrders
    },
    groupBySku (skus) {
      const groupped = skus.reduce((acc, sku) => {
        const index = acc.findIndex((item) => item.id === sku.id)

        if (index === -1) {
          acc.push({
            ...sku,
            importAmount: sku?.importAmount || 0,
            importUnitPrice: sku?.importUnitPrice || 0
          })
        } else {
          acc[index].amount += sku.amount
          acc[index].importAmount += sku?.importAmount || 0
          acc[index].importUnitPrice = sku?.importUnitPrice || acc[index].importUnitPrice
          acc[index].price += sku.price
          acc[index].fullPrice += sku.fullPrice
        }

        return acc
      }, [])

      return groupped.map((sku) => {
        const importPrice = sku.importUnitPrice * sku.importAmount
        const rawAmount = sku.amount - sku.importAmount
        const unitPrice = rawAmount ? (sku.price - importPrice) / rawAmount : (sku.fullPrice / sku.amount)
        const fullPrice = unitPrice * sku.amount
        const price = (unitPrice * rawAmount) + importPrice
        const averageUnitPrice = Number((fullPrice / sku.amount).toFixed(2))
        const discount = fullPrice - price

        return {
          ...sku,
          fullPrice,
          price,
          unitPrice: averageUnitPrice,
          discountPrice: discount < 0 ? 0 : discount,
          discountType: 'amount'
        }
      })
    },
    onSubmitImport (skus) {
      const grouppedSkus = this.groupBySku(skus)
      const subOrders = this.mapSubOrders(skus)

      this.setSkus(grouppedSkus)
      this.setOrderDetail({
        ...this.orderDetail,
        promotionCode: {},
        discount: 0,
        skus: grouppedSkus,
        subOrders
      })

      this.onCloseCsvDialog()
    },
    async downloadTemplateCsv () {
      const mapped = [
        this.masterHeaderCsv.reduce((obj, header) => ({ ...obj, [header]: '' }), {})
      ]

      const options = {
        filename: 'order-import-template',
        showLabels: true,
        useKeysAsHeaders: true
      }

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(mapped)
    }
  }
}
</script>

<style scoped>

</style>
